// components/common/Loader.jsx — Animated Bike Loader
import React from 'react';

const Loader = ({
  text = "Finding your perfect ride...",
  fullScreen = false,
  className = ""
}) => {
  const wrap = fullScreen
    ? "fixed inset-0 z-[9999] flex flex-col items-center justify-center"
    : "flex flex-col items-center justify-center py-20";

  return (
    <div
      className={`${wrap} ${className}`}
      style={fullScreen ? { background: 'linear-gradient(135deg,#0f172a 0%,#1e1b3a 60%,#1a0808 100%)' } : {}}
    >
      <style>{`
        @keyframes bikeMove   { 0%{transform:translateX(-50px) rotate(-1.5deg)} 100%{transform:translateX(50px) rotate(1.5deg)} }
        @keyframes wheelTurn  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes riderBob   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        @keyframes roadDash   { 0%{transform:translateX(0)} 100%{transform:translateX(-64px)} }
        @keyframes speedLine  { 0%{opacity:.8;transform:scaleX(1)} 100%{opacity:0;transform:scaleX(0)} transform-origin:right }
        @keyframes dotPop     { 0%,100%{transform:translateY(0);opacity:1} 50%{transform:translateY(-12px);opacity:.7} }
        @keyframes shimmer    { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes glowPulse  { 0%,100%{box-shadow:0 0 20px rgba(30,64,175,.4)} 50%{box-shadow:0 0 40px rgba(220,38,38,.5),0 0 20px rgba(30,64,175,.3)} }
      `}</style>

      {/* Card */}
      <div
        className="relative flex flex-col items-center px-10 py-10 rounded-3xl"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(16px)',
          animation: 'glowPulse 2.5s ease-in-out infinite',
          minWidth: 280
        }}
      >
        {/* Road scene */}
        <div style={{ width: 220, height: 110, position: 'relative', marginBottom: 8 }}>

          {/* Speed lines (behind bike) */}
          <div style={{ position: 'absolute', left: 10, top: 30, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {[28, 18, 10].map((w, i) => (
              <div key={i} style={{
                width: w, height: 3, borderRadius: 99,
                background: 'linear-gradient(90deg,rgba(59,130,246,.8),transparent)',
                animation: `speedLine 0.6s ease-in-out infinite`,
                animationDelay: `${i * 0.12}s`
              }}/>
            ))}
          </div>

          {/* Bike — animates left↔right */}
          <div style={{ position: 'absolute', top: 10, left: 50, animation: 'bikeMove 1.4s ease-in-out infinite alternate' }}>
            {/* Rider bob */}
            <div style={{ animation: 'riderBob 0.7s ease-in-out infinite', transformOrigin: 'bottom center' }}>
              <svg width="120" height="80" viewBox="0 0 120 80" fill="none">
                {/* Back wheel */}
                <g style={{ animation: 'wheelTurn 0.4s linear infinite', transformOrigin: '22px 62px' }}>
                  <circle cx="22" cy="62" r="16" stroke="#1e40af" strokeWidth="4" fill="none"/>
                  <circle cx="22" cy="62" r="6"  fill="#1e40af"/>
                  <line x1="22" y1="46" x2="22" y2="78" stroke="#1e40af" strokeWidth="2"/>
                  <line x1="6"  y1="62" x2="38" y2="62" stroke="#1e40af" strokeWidth="2"/>
                </g>
                {/* Front wheel */}
                <g style={{ animation: 'wheelTurn 0.4s linear infinite', transformOrigin: '96px 62px' }}>
                  <circle cx="96" cy="62" r="16" stroke="#dc2626" strokeWidth="4" fill="none"/>
                  <circle cx="96" cy="62" r="6"  fill="#dc2626"/>
                  <line x1="96" y1="46" x2="96" y2="78" stroke="#dc2626" strokeWidth="2"/>
                  <line x1="80" y1="62" x2="112" y2="62" stroke="#dc2626" strokeWidth="2"/>
                </g>
                {/* Frame */}
                <path d="M22 62 L48 30 L80 30 L96 62" stroke="#1e40af" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M48 30 L22 62"                stroke="#3b82f6"  strokeWidth="3" strokeLinecap="round"/>
                <path d="M68 30 L96 62"                stroke="#dc2626"  strokeWidth="3" strokeLinecap="round"/>
                {/* Handlebar */}
                <path d="M80 30 L92 20 L104 22"        stroke="#1e3a8a"  strokeWidth="3" strokeLinecap="round"/>
                {/* Seat */}
                <path d="M46 29 L62 29"                stroke="#dc2626"  strokeWidth="5" strokeLinecap="round"/>
                {/* Rider body */}
                <ellipse cx="62" cy="18" rx="10" ry="12" fill="#dc2626" opacity="0.9"/>
                {/* Helmet */}
                <circle cx="62" cy="10" r="9" fill="#1e40af"/>
                <path d="M54 10 Q62 2 70 10" fill="#3b82f6" opacity="0.6"/>
                {/* Arms to handlebar */}
                <path d="M64 20 L92 22"  stroke="#dc2626" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
          </div>

          {/* Road */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 22,
            background: 'linear-gradient(90deg,#1e3a8a,#1e40af,#7f1d1d,#dc2626)',
            borderRadius: 11,
            overflow: 'hidden'
          }}>
            {/* Road shine */}
            <div style={{ position: 'absolute', top: 3, left: 0, right: 0, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 99 }}/>
            {/* Dashes */}
            <div style={{
              position: 'absolute', top: '50%', left: 0,
              display: 'flex', gap: 16,
              transform: 'translateY(-50%)',
              animation: 'roadDash 0.5s linear infinite'
            }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} style={{ width: 32, height: 3, background: 'rgba(255,255,255,0.5)', borderRadius: 99, flexShrink: 0 }}/>
              ))}
            </div>
          </div>
        </div>

        {/* Bouncing dots */}
        <div className="flex items-end gap-2 mb-5" style={{ height: 24 }}>
          {['#1e40af','#3b82f6','#dc2626'].map((color, i) => (
            <div key={i} style={{
              width: 10, height: 10, borderRadius: '50%', background: color,
              animation: `dotPop 0.8s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`
            }}/>
          ))}
        </div>

        {/* Text */}
        <p style={{
          fontWeight: 700, fontSize: 14, letterSpacing: '0.05em',
          background: 'linear-gradient(90deg,#60a5fa,#f87171)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {text}
        </p>

        {/* Shimmer bar */}
        <div style={{
          marginTop: 12, width: '100%', height: 3, borderRadius: 99,
          background: 'linear-gradient(90deg,#1e40af30,#3b82f6,#dc262630,#dc2626,#1e40af30)',
          backgroundSize: '400px 3px',
          animation: 'shimmer 1.6s linear infinite'
        }}/>
      </div>
    </div>
  );
};

export default Loader;
